/* eslint-disable @typescript-eslint/require-await */
import Joi from '@hapi/joi';
import React from 'react';
import { connect } from 'react-redux';
import { api570SelectOptions } from '../../../customInspectionTypes/api570/api570SelectOptions';
import { defaultSelectOptions } from '../../../customInspectionTypes/default/defaultSelectOptions';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { ILanguageCodeProps, IValidationComponentState, ValidatingComponent } from '../../../mixins/validation/validation';
import { getCompany } from '../../../store/company/actions';
import { fetchGet, fetchJson } from '../../../store/fetch';
import { AssetTypes as assetTypes } from '../../../utils/enums/AssetTypes';
import { InspectionStandardTypes } from '../../../utils/enums/InspectionStandardTypes';
import { SurveyEditDisplayTypes } from '../../../utils/enums/SurveyEditDisplayTypes';
import { IConnectProps } from '../../../utils/IConnectProps';
import { isEemuaOrApi } from '../../../utils/isEemuaOrApi';
import { CollapsiblePanel, EditPanel, Panel, PanelBody, PanelBodyText, PanelButtonLink, PanelHeader, Tray } from '../../elements';
import { DraggableContainer } from '../../react-smooth-dnd/DraggableContainer';
import { DraggableItem } from '../../react-smooth-dnd/DraggableItem';
import { showAssetInNewWindow } from '../../utils/showAssetInNewWindow';
import { AssetDropzone } from '../Common/AssetDropzone';
import { AssetTemplate } from '../Common/AssetTemplate';
import ConditionEdit from '../Common/ConditionEdit';
import { getAssetOrdersAfterReorder } from '../Common/getAssetOrdersAfterReorder';
import Remarks from '../Common/Remarks';
import { SurveyFmecaNenMaintenanceMeasures } from '../Common/SurveyFmecaNenMaintenanceMeasures';
import DefectListEemua from '../EEMUA/DefectListEEMUA';
import { Defect } from '../Interfaces/Defect';
import { IAsset } from '../Interfaces/IAsset';
import { ICategory } from '../Interfaces/ICategory';
import { IElement } from '../Interfaces/IElement';
import { INenDefect } from '../Interfaces/INenDefect';
import DefectAddEdit from '../NEN/DefectAddEdit';
import DefectListNen from '../NEN/DefectListNEN';
import { CompressedFiles } from '../Worker/CompressedFiles';

interface IRemark {
    id: string;
    text: string;
    remarkedOn: Date;
    remarkedBy: string;
}

interface ISurveyEditVisualsAndAssessmentProps extends ITranslateProps, IConnectProps, ILanguageCodeProps {
    surveyId: string;
    objectId: string;
    displayType: SurveyEditDisplayTypes;
    inspectionStandardType: InspectionStandardTypes;
    objectInspectionStandardId?: undefined | string;
    isAssetDescriptionVisible?: undefined | boolean;
    languageCode: string;
    elementId?: undefined | string;
    unitId?: undefined | string;
    manifestationId?: undefined | string;
    archiveSelected: boolean;
    categoryId: string | undefined;
    categories: ICategory[];
    elements: IElement[];
    isFmecaAvailable: boolean;
    onItemChanged(): void;
}

interface ISurveyEditVisualsAndAssessmentState extends IValidationComponentState {
    storageUsed?: undefined | number;

    objectName?: undefined | string;
    objectCompositionIsVisible?: undefined | boolean;

    assets: IAsset[];
    remarks: IRemark[];

    breakDownTreeItemOpenState: { [key: string]: boolean };
    breakDownCategoryOpenState: { [key: string]: boolean };
    activeElementId?: undefined | string;
    activeUnitId?: undefined | string;
    activeManifestationId?: undefined | string;
    activeCode?: undefined | string;
    activeName?: undefined | string;
    elementHasUnits: boolean;
    unitHasManifestations: boolean;
    remarkText?: undefined | string;
    conditionId?: undefined | string;
    conditionAdvice?: undefined | string;
    score?: undefined | string;
    careScore?: undefined | string;
    isFurtherInvestigation?: undefined | boolean;
    investigationPriority?: undefined | string;
    defects: Defect[];
    defectId?: undefined | string;
    defectName?: undefined | string;
    defectCode?: undefined | string;
    defectDescription?: undefined | string;
    defectSeriousness?: undefined | number;
    defectIntensity?: undefined | number;
    defectExtend?: undefined | number;
    defectIsOk?: undefined | boolean;
    nenDefects: INenDefect[];
    defectAddEdit: 'hidden' | 'add' | 'edit';
    selectedDefect?: undefined | Defect;
    isUploading?: undefined | boolean;
    imageListSize: number;
}

function mapStateToProps(state: any) {
    const { language } = state;
    return {
        languageCode: language.code,
    };
}

const thumbnailPollIntervalMs = 5000;
const thumbnailPollMaxRetries = 30;
const thumbnailAssetTypes = new Set([assetTypes.Image, assetTypes.Image360, assetTypes.ImageTif, assetTypes.MeasurementPDF]);

class SurveyEditVisualsAndAssessment extends ValidatingComponent<ISurveyEditVisualsAndAssessmentProps, ISurveyEditVisualsAndAssessmentState> {
    private thumbnailPollTimer: ReturnType<typeof setTimeout> | undefined;
    private thumbnailPollRetries = 0;

    constructor(props: ISurveyEditVisualsAndAssessmentProps) {
        super(props);

        this.state = {
            submitting: false,
            failed: false,
            saved: false,
            breakDownTreeItemOpenState: {},
            breakDownCategoryOpenState: {},
            assets: [],
            remarks: [],
            defects: [],
            nenDefects: [],
            defectAddEdit: 'hidden',
            elementHasUnits: false,
            unitHasManifestations: false,
            errors: {},
            imageListSize: 100,
        };
    }

    public getValidatorTypes(): any {
        return {
            cost: Joi.number().allow('').label(this.props.t('survey.conditionCost')),
            costYear: Joi.number().allow('').integer().label(this.props.t('survey.conditionCostYear')),
            score: Joi.string().required().label(this.props.t('survey.conditionScore')),
        };
    }

    public getValidatorData() {
        return {
            advice: this.state.conditionAdvice,
            score: this.state.score,
        };
    }

    public override async componentDidMount() {
        if (this.props.elementId) {
            await this.onElementClicked(this.props.elementId);
        }
        if (this.props.unitId) {
            await this.onUnitClicked(this.props.unitId);
        }
        if (this.props.manifestationId) {
            await this.onManifestationClicked(this.props.manifestationId);
        }
        if (this.props.categoryId) {
            await this.onCategoryClicked(this.props.categoryId);
        }
        if (this.props.archiveSelected) {
            await this.onArchiveClicked();
        }
        await this.loadNenDefects();
    }

    public override componentWillUnmount() {
        if (this.thumbnailPollTimer) {
            clearTimeout(this.thumbnailPollTimer);
        }
    }

    public override async componentDidUpdate(prevProps: ISurveyEditVisualsAndAssessmentProps) {
        if (prevProps.elementId !== this.props.elementId && this.props.elementId) {
            await this.onElementClicked(this.props.elementId);
        }
        if (prevProps.unitId !== this.props.unitId) {
            if (this.props.unitId) {
                await this.onUnitClicked(this.props.unitId);
            } else if (this.props.elementId) {
                await this.onElementClicked(this.props.elementId);
            }
        }
        if (prevProps.manifestationId !== this.props.manifestationId) {
            if (this.props.manifestationId) {
                await this.onManifestationClicked(this.props.manifestationId);
            } else if (this.props.unitId) {
                await this.onUnitClicked(this.props.unitId);
            } else if (this.props.elementId) {
                await this.onElementClicked(this.props.elementId);
            }
        }
        if (prevProps.categoryId !== this.props.categoryId) {
            await this.onCategoryClicked(this.props.categoryId);
        }
        if (this.props.archiveSelected && !prevProps.archiveSelected) {
            await this.onArchiveClicked();
        }
    }

    public override render() {
        const isNen = this.props.inspectionStandardType === InspectionStandardTypes.Nen2767;
        const setAssetsInView = this.state.assets.slice(0, this.state.imageListSize);
        return (
            <Tray>
                {this.renderMessage()}
                <Panel>
                    {(!this.showPanel() && (
                        <>
                            {(this.props.displayType === SurveyEditDisplayTypes.Visuals && (
                                <PanelBodyText text={this.props.t('survey.selectInObjectTreeVisual')} />
                            )) || <PanelBodyText text={this.props.t('survey.selectInObjectTreeAssessment')} />}
                        </>
                    )) || (
                        <PanelBody>
                            <PanelHeader text={this.state.activeName} />
                            {this.props.displayType === SurveyEditDisplayTypes.Visuals && (
                                <div className="pr20 pl20">
                                    <AssetDropzone
                                        acceptedFiles=".png,.jpeg,.jpg,.jfif,.jfi,.xls,.xlsx,.pdf"
                                        onComplete={this.onDropzoneComplete.bind(this)}
                                        onError={this.onDropzoneError.bind(this)}
                                        objectId={this.props.objectId}
                                        surveyId={this.props.surveyId}
                                        elementId={this.state.activeElementId}
                                        manifestationId={this.state.activeManifestationId}
                                        unitId={this.state.activeUnitId}
                                        autoProcessQueue={true}
                                        parallelUploads={10}
                                        maxFileSizeMb={500}
                                        onSending={async () => this.setState({ isUploading: true })}
                                        onQueueComplete={async () => this.setState({ isUploading: false })}
                                    />
                                </div>
                            )}

                            {(this.state.assets.length === 0 && (
                                <>
                                    {this.props.displayType === SurveyEditDisplayTypes.Assessment && (
                                        <p className="text-center mb30"> {this.props.t('survey.noImages')} </p>
                                    )}
                                </>
                            )) || (
                                <div className="section row pt10 pr20 pl20 bg-light dark">
                                    <DraggableContainer
                                        onDrop={async (result: any) => this.handleAssetsSorted(result)}
                                        orientation="horizontal"
                                        dragHandleSelector=".column-drag-handle"
                                    >
                                        {setAssetsInView.map((asset, i) => (
                                            <DraggableItem key={asset.id} className="col-sm-6 col-md-3">
                                                <AssetTemplate
                                                    key={asset.id}
                                                    {...asset}
                                                    order={i}
                                                    objectId={this.props.objectId}
                                                    surveyId={this.props.surveyId}
                                                    onShowAsset={this.onShowAsset.bind(this)}
                                                    onAssetTextChange={this.handleAssetTextChange.bind(this)}
                                                    onAssetsChanged={this.handleRemoveAsset.bind(this)}
                                                    onAssetsSorted={this.handleAssetsSorted.bind(this)}
                                                    displayType={this.props.displayType}
                                                    submitting={this.state.submitting}
                                                    isEemuaOrApi={isEemuaOrApi(this.props.inspectionStandardType)}
                                                    canRemove={true}
                                                    canAddAnnotations={true}
                                                    returnUrl={window.location.pathname}
                                                    canAddToReport={this.props.displayType === SurveyEditDisplayTypes.Assessment}
                                                    canAddToReportDocument={this.props.displayType === SurveyEditDisplayTypes.Assessment}
                                                    canEditDescription={this.props.isAssetDescriptionVisible}
                                                    canCopyAndMove={true}
                                                    canSort={true}
                                                    canEditExternalUrl={true}
                                                    canAddToStartPage
                                                />
                                            </DraggableItem>
                                        ))}
                                    </DraggableContainer>
                                    {this.state.assets.length > this.state.imageListSize && (
                                        <div className="text-center mb20">
                                            <div className="fw600 fs12 m20">
                                                {this.props.t(
                                                    'survey.imagesDisplayed',
                                                    this.state.imageListSize.toString(),
                                                    this.state.assets.length.toString(),
                                                )}
                                            </div>
                                            <PanelButtonLink
                                                onClick={async () => this.setState({ imageListSize: this.state.imageListSize + 100 })}
                                                text={this.props.t('survey.imagesLoad')}
                                                icon="fa fa-refresh"
                                                small={true}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {this.props.displayType === SurveyEditDisplayTypes.Visuals &&
                                this.state.assets.length !== 0 &&
                                !this.props.archiveSelected && (
                                    <div className="text-right">
                                        <a
                                            href="#"
                                            className="btn btn-primary btn-sm fw600 ml10"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (window.confirm(this.props.t('common.confirmMultiDelete'))) {
                                                    void this.removeAllAssets(e);
                                                }
                                            }}
                                        >
                                            <span className="fa fa-trash-o pr5"></span> {this.props.t('survey.removeAllFiles')}
                                        </a>
                                    </div>
                                )}

                            {this.showConditionDefect() && (
                                <>
                                    {this.props.displayType === SurveyEditDisplayTypes.Assessment &&
                                        isEemuaOrApi(this.props.inspectionStandardType) && (
                                            <ConditionEdit
                                                score={this.state.score}
                                                conditionAdvice={this.state.conditionAdvice ?? ''}
                                                investigationPriority={this.state.investigationPriority}
                                                objectInspectionStandardType={this.props.inspectionStandardType}
                                                handleChange={this.handleChange.bind(this)}
                                                removeConditionClicked={this.removeConditionClicked.bind(this)}
                                                saveConditionClicked={this.saveConditionClicked.bind(this)}
                                                conditionOptions={
                                                    this.props.inspectionStandardType === InspectionStandardTypes.Api570
                                                        ? api570SelectOptions.api570Score
                                                        : defaultSelectOptions.defaultScore
                                                }
                                                canSetConditionScoreWhenDefects={true}
                                                canSetInvestigationPriority={false}
                                            />
                                        )}

                                    {this.props.displayType === SurveyEditDisplayTypes.Assessment &&
                                        isEemuaOrApi(this.props.inspectionStandardType) && (
                                            <DefectListEemua
                                                defects={this.state.defects}
                                                defectAddEdit={this.state.defectAddEdit}
                                                onEditDefect={(defect) => this.onEditDefect(defect)}
                                                onRemoveDefect={this.onRemoveDefect.bind(this)}
                                                onAddDefectClicked={this.onAddDefectClicked.bind(this)}
                                            />
                                        )}

                                    {this.props.displayType === SurveyEditDisplayTypes.Assessment &&
                                        !isEemuaOrApi(this.props.inspectionStandardType) && (
                                            <DefectListNen
                                                selectedDefect={this.state.selectedDefect}
                                                defects={this.state.defects}
                                                defectAddEdit={this.state.defectAddEdit}
                                                useCareScore={isNen}
                                                onEditDefect={(defect) => this.onEditDefect(defect)}
                                                onRemoveDefect={this.onRemoveDefect.bind(this)}
                                                onAddDefectClicked={this.onAddDefectClicked.bind(this)}
                                            />
                                        )}

                                    {this.props.displayType === SurveyEditDisplayTypes.Assessment && this.state.defectAddEdit !== 'hidden' && (
                                        <DefectAddEdit
                                            mode={this.state.defectAddEdit}
                                            inspectionStandardType={this.props.inspectionStandardType}
                                            defect={this.state.selectedDefect}
                                            nenDefects={this.state.nenDefects}
                                            isManifestation={this.props.manifestationId && this.props.manifestationId !== ''}
                                            useCareScore={isNen}
                                            languageCode={this.props.languageCode}
                                            onSave={this.saveDefect.bind(this)}
                                            onCancel={this.cancelDefect.bind(this)}
                                        />
                                    )}

                                    {this.props.displayType === SurveyEditDisplayTypes.Assessment &&
                                        !isEemuaOrApi(this.props.inspectionStandardType) && (
                                            <ConditionEdit
                                                score={this.state.score}
                                                careScore={this.state.careScore}
                                                conditionAdvice={this.state.conditionAdvice!}
                                                investigationPriority={this.state.investigationPriority}
                                                isFurtherInvestigation={this.state.isFurtherInvestigation}
                                                objectInspectionStandardType={this.props.inspectionStandardType}
                                                handleChange={this.handleChange.bind(this)}
                                                removeConditionClicked={this.removeConditionClicked.bind(this)}
                                                saveConditionClicked={this.saveConditionClicked.bind(this)}
                                                conditionOptions={isNen ? defaultSelectOptions.noScore : defaultSelectOptions.defaultScore}
                                                canSetConditionScoreWhenDefects={this.state.defects.length === 0 || !isNen}
                                                canSetInvestigationPriority={true}
                                            />
                                        )}
                                </>
                            )}
                            {!this.props.archiveSelected && (
                                <>
                                    {this.props.displayType === SurveyEditDisplayTypes.Assessment && isNen && !this.state.activeElementId && (
                                        <>
                                            <PanelHeader text={this.props.t('maintenanceMeasure.maintenanceMeasures')} />
                                            <SurveyFmecaNenMaintenanceMeasures
                                                surveyId={this.props.surveyId}
                                                unitId={this.state.activeUnitId}
                                                manifestationId={this.state.activeManifestationId}
                                                inspectionStandardType={this.props.inspectionStandardType}
                                                surveyIsFmecaAvailable={this.props.isFmecaAvailable}
                                            />
                                        </>
                                    )}
                                    <Remarks
                                        remarks={this.state.remarks}
                                        remarkText={this.state.remarkText ?? ''}
                                        onChangeEditor={this.onChangeEditor.bind(this)}
                                        saveRemarkClicked={this.saveRemarkClicked.bind(this)}
                                    />
                                </>
                            )}
                        </PanelBody>
                    )}
                </Panel>
                {this.props.archiveSelected && this.props.displayType === SurveyEditDisplayTypes.Visuals && (
                    <EditPanel bottomMargin={true} noPadding={true}>
                        <CollapsiblePanel heading={{ dark: true, text: this.props.t('worker.uploadZip'), initialIsClosed: true }}>
                            <PanelBody isForm={true}>
                                <CompressedFiles
                                    surveyId={this.props.surveyId}
                                    inspectionStandardType={this.props.inspectionStandardType}
                                    objectId={this.props.objectId}
                                    onConverted={async () => {
                                        await this.loadAssets(this.state.activeElementId, this.state.activeUnitId, this.state.activeManifestationId);
                                        this.props.onItemChanged();
                                    }}
                                />
                            </PanelBody>
                        </CollapsiblePanel>
                    </EditPanel>
                )}
            </Tray>
        );
    }

    private showPanel() {
        if (this.state.activeElementId) {
            return true;
        } else if (this.state.activeUnitId) {
            return true;
        } else if (this.state.activeManifestationId) {
            return true;
        } else if (this.props.archiveSelected && this.props.displayType === SurveyEditDisplayTypes.Visuals) {
            return true;
        }
        return false;
    }

    private showConditionDefect() {
        if (this.state.activeElementId) {
            if (this.props.displayType === SurveyEditDisplayTypes.Assessment) {
                if (this.props.inspectionStandardType === InspectionStandardTypes.Nen2767) {
                    return false;
                } else if (isEemuaOrApi(this.props.inspectionStandardType)) {
                    return true;
                } else if (this.state.elementHasUnits) {
                    return false;
                }
            }
            return true;
        } else if (this.state.activeUnitId) {
            if (this.props.displayType === SurveyEditDisplayTypes.Assessment) {
                return true;
            }
            return true;
        } else if (this.state.activeManifestationId) {
            return true;
        }
        return false;
    }

    private async handleAssetsSorted(sortResult: { removedIndex: number; addedIndex: number }) {
        const assetOrders = getAssetOrdersAfterReorder(this.state.assets, sortResult);

        await this.setStatePromised({ submitting: true, failed: false, errorCode: undefined });
        const url = `/api/assets/order?surveyId=${this.props.surveyId}`;
        const result = await fetchJson(url, 'POST', { assetOrders });
        if (result.successful) {
            await this.setStatePromised({ submitting: false });
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        }
    }

    private onShowAsset(e: React.MouseEvent<any>, asset: IAsset) {
        e.preventDefault();
        showAssetInNewWindow(asset);
    }

    private handleAssetTextChange(description: string, assetId: string) {
        const elementPos = this.state.assets.map((item: any) => item.id).indexOf(assetId);
        this.state.assets[elementPos]!.description = description;
        void this.setState(this.state);
    }

    private onAddDefectClicked() {
        void this.setState({ defectAddEdit: 'add', selectedDefect: undefined });
    }

    private onDropzoneError(errorCode: string) {
        void this.setState({ submitting: false, errorCode, failed: true });
    }

    private async onDropzoneComplete() {
        await this.loadAssets(this.state.activeElementId, this.state.activeUnitId, this.state.activeManifestationId);
        this.props.dispatch(getCompany());
        await this.loadSize();
        this.props.onItemChanged();
        this.startThumbnailPolling();
    }

    private startThumbnailPolling() {
        this.thumbnailPollRetries = 0;
        this.pollForThumbnails();
    }

    private pollForThumbnails() {
        if (this.thumbnailPollTimer) {
            clearTimeout(this.thumbnailPollTimer);
        }
        const hasPendingThumbnails = this.state.assets.some((asset) => thumbnailAssetTypes.has(asset.type) && !asset.filenameThumbnail);
        if (!hasPendingThumbnails || this.thumbnailPollRetries >= thumbnailPollMaxRetries) {
            return;
        }
        this.thumbnailPollRetries++;
        this.thumbnailPollTimer = setTimeout(() => {
            this.loadAssets(this.state.activeElementId, this.state.activeUnitId, this.state.activeManifestationId)
                .then(() => this.pollForThumbnails())
                .catch((error) => console.log('Error polling for thumbnails', error));
        }, thumbnailPollIntervalMs);
    }

    private onChangeEditor(remarkText: string) {
        void this.setState({ remarkText });
    }

    private async removeAllAssets(e: any) {
        e.preventDefault();

        let url = `/api/removeAllAssets/?surveyId=${this.props.surveyId}`;
        if (this.state.activeElementId) {
            url += `&elementId=${this.state.activeElementId}`;
        }
        if (this.state.activeUnitId) {
            url += `&unitId=${this.state.activeUnitId}`;
        }
        if (this.state.activeManifestationId) {
            url += `&manifestationId=${this.state.activeManifestationId}`;
        }
        await this.setStatePromised({ submitting: true });
        const result = await fetchJson(url, 'DELETE');
        if (result.successful) {
            await this.loadAssets(this.state.activeElementId, this.state.activeUnitId, this.state.activeManifestationId);
            this.props.dispatch(getCompany());
            await this.loadSize(); // display total size for survey
            await this.setStatePromised({ submitting: false });
            this.props.onItemChanged();
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        }
    }

    private async handleRemoveAsset() {
        // const url = `/api/surveys/${this.props.surveyId}/assets/${id}`;
        await this.setStatePromised({ submitting: true });
        // await fetchJsonWithThrow(url, 'DELETE');
        await this.loadAssets(this.state.activeElementId, this.state.activeUnitId, this.state.activeManifestationId);
        this.props.dispatch(getCompany());
        await this.loadSize(); // display total size for survey
        await this.setStatePromised({ submitting: false });
        this.props.onItemChanged();
    }

    private async onCategoryClicked(categoryId: string | undefined) {
        if (categoryId === undefined) {
            return;
        }
        const { breakDownCategoryOpenState } = this.state;
        breakDownCategoryOpenState[categoryId] = !breakDownCategoryOpenState[categoryId];
        await this.setStatePromised({ breakDownCategoryOpenState });
    }

    private getElement(elementId: string) {
        return this.props.elements.find((i) => i.id === elementId);
    }

    private async onElementClicked(elementId: string) {
        const element = this.getElement(elementId);
        if (!element) {
            return;
        }

        const { breakDownTreeItemOpenState } = this.state;
        breakDownTreeItemOpenState[element.id] = !breakDownTreeItemOpenState[element.id];
        await this.setStatePromised({ breakDownTreeItemOpenState });

        await this.setStatePromised({
            activeElementId: element.id,
            activeCode: element.code,
            activeName: element.name,
            activeUnitId: undefined,
            activeManifestationId: undefined,
            elementHasUnits: element.units.length > 0,
        });
        await this.loadAssets(element.id);
        await this.loadRemarks(element.id);
        if (element.units.length === 0 || isEemuaOrApi(this.props.inspectionStandardType)) {
            await this.loadCondition(element.id);
            // await this.loadObjectTypeDefects(element.code, element.id);
            this.cancelDefect();
        }
    }

    private getUnit(unitId: string) {
        for (const element of this.props.elements) {
            for (const unit of element.units) {
                if (unit.id === unitId) {
                    return unit;
                }
            }
        }
        return undefined;
    }

    private getManifestation(manifestationId: string) {
        for (const element of this.props.elements) {
            for (const unit of element.units) {
                for (const manifestation of unit.manifestations) {
                    if (manifestation.id === manifestationId) {
                        return manifestation;
                    }
                }
            }
        }
        return undefined;
    }

    private async onManifestationClicked(manifestationId: string) {
        const manifestation = this.getManifestation(manifestationId);

        if (!manifestation) {
            return;
        }

        await this.setStatePromised({
            activeUnitId: undefined,
            activeManifestationId: manifestation.id,
            activeCode: manifestation.code,
            activeName: manifestation.name,
            activeElementId: undefined,
        });

        await this.loadAssets(undefined, undefined, manifestation.id);
        await this.loadRemarks(undefined, undefined, manifestation.id);
        await this.loadCondition(undefined, undefined, manifestation.id);
        // const unit = this.getUnit(manifestation.unitId);
        // await this.loadObjectTypeDefects(unit.code, undefined, manifestation.unitId);
        this.cancelDefect();
    }

    private async onArchiveClicked() {
        if (!this.props.archiveSelected) {
            return;
        }

        await this.setStatePromised({
            activeElementId: undefined,
            activeUnitId: undefined,
            activeManifestationId: undefined,
            activeCode: undefined,
            activeName: this.props.t('common.archive'),
        });

        await this.loadAssets();
    }

    private async onUnitClicked(unitId: string) {
        const unit = this.getUnit(unitId);

        if (!unit) {
            return;
        }

        const { breakDownTreeItemOpenState } = this.state;
        breakDownTreeItemOpenState[unit.id] = !breakDownTreeItemOpenState[unit.id];
        await this.setStatePromised({ breakDownTreeItemOpenState });

        await this.setStatePromised({
            activeUnitId: unit.id,
            activeCode: unit.code,
            activeName: unit.name + (unit.material && unit.quantity ? `, ${unit.material} ${unit.quantity} ${unit.quantityUnitOfMeasurement} ` : ''),
            activeElementId: undefined,
            activeManifestationId: undefined,
            unitHasManifestations: unit.manifestations.length > 0,
        });
        await this.loadAssets(undefined, unit.id);
        await this.loadRemarks(undefined, unit.id);
        await this.loadCondition(undefined, unit.id);
        // await this.loadObjectTypeDefects(unit.code, undefined, unit.id);
        this.cancelDefect();
    }

    private async loadRemarks(elementId?: string, unitId?: string, manifestationId?: string) {
        let url = `/api/surveys/${this.props.surveyId}/`;
        if (elementId) {
            url += `elements/${elementId}/remarks`;
        }
        if (unitId) {
            url += `units/${unitId}/remarks`;
        }
        if (manifestationId) {
            url += `manifestations/${manifestationId}/remarks`;
        }
        await this.setStatePromised({ submitting: true });
        const result = await fetchJson(url, 'GET');
        if (result.successful) {
            await this.setStatePromised({ remarks: result.json, submitting: false, failed: false });
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        }
    }

    private async loadDefects(conditionId: string, elementId?: string, unitId?: string, manifestationId?: string) {
        let url = `/api/surveys/${this.props.surveyId}/`;
        if (elementId) {
            url += `elements/${elementId}/condition/${conditionId}/defects`;
        }
        if (unitId) {
            url += `units/${unitId}/condition/${conditionId}/defects`;
        }
        if (manifestationId) {
            url += `manifestations/${manifestationId}/condition/${conditionId}/defects`;
        }
        await this.setStatePromised({ submitting: true });
        const result = await fetchJson(url, 'GET');
        if (result.successful) {
            result.json.repairDate = new Date(result.json.repairDate);
            await this.setStatePromised({ defects: result.json, submitting: false });
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        }
    }

    private async loadNenDefects() {
        const url = '/api/nendefects';
        const result = await fetchJson(url, 'GET');
        if (result.successful) {
            await this.setStatePromised({ nenDefects: result.json, submitting: false });
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        }
    }

    private async loadCondition(elementId?: string, unitId?: string, manifestationId?: string) {
        let url = `/api/surveys/${this.props.surveyId}/`;
        if (elementId) {
            url += `elements/${elementId}/condition`;
        }
        if (unitId) {
            url += `units/${unitId}/condition`;
        }
        if (manifestationId) {
            url += `manifestations/${manifestationId}/condition`;
        }
        await this.setStatePromised({ submitting: true });
        const result = await fetchJson(url, 'GET');

        if (!result.successful) {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        } else if (result.json.id) {
            await this.setStatePromised({
                submitting: false,
                conditionAdvice: result.json.advice,
                conditionId: result.json.id,
                investigationPriority: result.json.investigationPriority,
                isFurtherInvestigation: result.json.isFurtherInvestigation,
                score: result.json.score,
                careScore: result.json.careScore,
            });
            await this.loadDefects(result.json.id, elementId, unitId, manifestationId);
        } else {
            await this.setStatePromised({
                submitting: false,
                conditionAdvice: '',
                conditionId: undefined,
                score: '',
                careScore: '',
                defects: [],
            });
        }
    }

    private async loadSize() {
        const url = `/api/surveysize/${this.props.surveyId}`;
        await this.setStatePromised({ submitting: true });
        const result = await fetchJson(url, 'GET');
        if (result.successful) {
            await this.setStatePromised({ storageUsed: result.json.storageUsed, submitting: false });
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        }
    }

    private async loadAssets(elementId?: string, unitId?: string, manifestationId?: string) {
        let url = `/api/assets?surveyId=${this.props.surveyId}&types=${assetTypes.Video},${assetTypes.Image},${assetTypes.Image360},${assetTypes.DerivedVideo},${assetTypes.DerivedImage},${assetTypes.MeasurementXLS},${assetTypes.MeasurementPDF},${assetTypes.ImageAnnotation}`;
        if (elementId) {
            url += `&elementId=${elementId}`;
        }
        if (unitId) {
            url += `&unitId=${unitId}`;
        }
        if (manifestationId) {
            url += `&manifestationId=${manifestationId}`;
        }
        if (!elementId && !unitId && !manifestationId) {
            url += '&unassigned=true';
        }

        await this.setStatePromised({ submitting: true });
        const result = await fetchGet(url, {}, ['404', '502']);
        await this.setStatePromised({ submitting: false, assets: result.json });
    }

    private onEditDefect(defect: Defect) {
        void this.setState({ selectedDefect: defect, defectAddEdit: 'edit' });
    }

    private async onRemoveDefect(defect: Defect) {
        let url = `/api/surveys/${this.props.surveyId}/`;
        if (this.state.activeElementId) {
            url += `elements/${this.state.activeElementId}/condition/${this.state.conditionId}/defects/${defect.id}`;
        }
        if (this.state.activeUnitId) {
            url += `units/${this.state.activeUnitId}/condition/${this.state.conditionId}/defects/${defect.id}`;
        }
        if (this.state.activeManifestationId) {
            url += `manifestations/${this.state.activeManifestationId}/condition/${this.state.conditionId}/defects/${defect.id}`;
        }
        await this.setStatePromised({ submitting: true, selectedDefect: undefined, defectAddEdit: 'hidden' });
        const result = await fetchJson(url, 'DELETE');
        if (result.successful) {
            await this.setStatePromised({ submitting: false });
            await this.loadDefects(this.state.conditionId!, this.state.activeElementId, this.state.activeUnitId, this.state.activeManifestationId);
            await this.loadCondition(this.state.activeElementId, this.state.activeUnitId, this.state.activeManifestationId);
            this.props.onItemChanged();
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        }
    }

    private cancelDefect() {
        void this.setState({ selectedDefect: undefined, defectAddEdit: 'hidden' });
    }

    private async saveDefect(defect: Defect) {
        let url = `/api/surveys/${this.props.surveyId}/`;
        defect.conditionId = this.state.conditionId;

        if (this.state.activeElementId) {
            url += `elements/${this.state.activeElementId}`;
        }
        if (this.state.activeUnitId) {
            url += `units/${this.state.activeUnitId}`;
        }
        if (this.state.activeManifestationId) {
            url += `manifestations/${this.state.activeManifestationId}`;
        }
        if (this.state.conditionId) {
            url += `/condition/${this.state.conditionId}`;
        }
        url += '/defects';
        if (defect.id) {
            url += `/${defect.id}`;
        }

        await this.setStatePromised({ submitting: true });
        delete defect.id;
        const result = await fetchJson(url, 'POST', defect);
        if (result.successful) {
            if (result.json.conditionId) {
                await this.setStatePromised({ conditionId: result.json.conditionId });
            }
            await this.setStatePromised({ submitting: false, defectAddEdit: 'hidden' });
            await this.loadDefects(
                result.json.conditionId ? result.json.conditionId : this.state.conditionId,
                this.state.activeElementId,
                this.state.activeUnitId,
                this.state.activeManifestationId,
            );
            if (!isEemuaOrApi(this.props.inspectionStandardType)) {
                await this.loadCondition(this.state.activeElementId, this.state.activeUnitId, this.state.activeManifestationId);
            }
            this.props.onItemChanged();
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        }
    }

    private async saveConditionClicked(e: any) {
        e.preventDefault();

        this.validate(async (validationError: any) => {
            if (!validationError) {
                await this.setStatePromised({ submitting: true });
                const data = {
                    advice: this.state.conditionAdvice,
                    score: this.state.score,
                    isFurtherInvestigation: this.state.isFurtherInvestigation,
                    investigationPriority: this.state.investigationPriority,
                };
                let url = `/api/surveys/${this.props.surveyId}/`;
                if (this.state.activeElementId) {
                    url += `elements/${this.state.activeElementId}/condition`;
                }
                if (this.state.activeUnitId) {
                    url += `units/${this.state.activeUnitId}/condition`;
                }
                if (this.state.activeManifestationId) {
                    url += `manifestations/${this.state.activeManifestationId}/condition`;
                }
                if (this.state.conditionId) {
                    url += `/${this.state.conditionId}`;
                }
                const result = await fetchJson(url, 'POST', data);
                if (result.successful) {
                    await this.setStatePromised({ submitting: false });
                    await this.loadCondition(this.state.activeElementId, this.state.activeUnitId, this.state.activeManifestationId);
                    this.props.onItemChanged();
                } else {
                    await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
                }
            }
        });
    }

    private async removeConditionClicked(e: any) {
        e.preventDefault();

        if (this.state.conditionId) {
            const url = `/api/surveys/${this.props.surveyId}/condition/${this.state.conditionId}`;
            await this.setStatePromised({ submitting: true });
            const result = await fetchJson(url, 'DELETE');
            if (result.successful) {
                await this.setStatePromised({ submitting: false });
                await this.loadCondition(this.state.activeElementId, this.state.activeUnitId, this.state.activeManifestationId);
                this.props.onItemChanged();
            } else {
                await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
            }
        }
    }

    private async saveRemarkClicked(e: any) {
        e.preventDefault();

        if (this.state.remarkText && this.state.remarkText !== '') {
            const data = { text: this.state.remarkText };

            let url = `/api/surveys/${this.props.surveyId}/`;
            if (this.state.activeElementId) {
                url += `elements/${this.state.activeElementId}/remarks`;
            }
            if (this.state.activeUnitId) {
                url += `units/${this.state.activeUnitId}/remarks`;
            }
            if (this.state.activeManifestationId) {
                url += `manifestations/${this.state.activeManifestationId}/remarks`;
            }
            await this.setStatePromised({ submitting: true });
            const result = await fetchJson(url, 'POST', data);
            if (result.successful) {
                await this.setStatePromised({ submitting: false, remarkText: '' });
                await this.loadRemarks(this.state.activeElementId, this.state.activeUnitId, this.state.activeManifestationId);
            } else {
                await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
            }
        }
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(
    connect(mapStateToProps, undefined, undefined, {
        forwardRef: true,
    })(SurveyEditVisualsAndAssessment),
);
